/*
 * all this code was based from original source:
 * https://github.com/jusbrasil/react-components/blob/5899a75977c7e76cb3572b10ddbcb38a0f37d974/packages/topic-popover/src/TopicPopover.jsx
 */
import { gql } from '@apollo/client'

import { UseTopicData_TopicFragment } from './__generated__/use-topic-data.graphql'

export type UseTopicDataProps = {
  topic: UseTopicData_TopicFragment | null
  context?: string
}

export type UseTopicDataReturn = {
  title: string
  titleWithNickname: string
  type: TopicDataType
  description: string | null
  image: string | null
  referenceUrl: string
  referenceDescription: string | null
  allowedLawUpperItems: UseTopicData_TopicFragment['lawUpperItems']
  allowedLawSubItems: UseTopicData_TopicFragment['lawSubItems']
  topicId: number | null
  topicType: string | null
  isRevoked: boolean
  relatedLegis: UseTopicData_TopicFragment['relatedLegis']
  relatedSumulasDocuments: UseTopicData_TopicFragment['relatedSumulasDocuments']
  lawItemType: string | null
} | null

type TopicDataType = 'common' | 'legislation' | 'definition'

const allowedLawItemTypes = [
  'EMENTA',
  'ARTIGO',
  'ALINEA',
  'PARAGRAFO',
  'INCISO',
  'NAO_IDENTIFICADO',
]

export function useTopicData(props: UseTopicDataProps): UseTopicDataReturn {
  const { topic, context } = props

  if (!topic) {
    return null
  }

  const {
    referenceLaw,
    definitions,
    lawAnchor,
    lawItemDescription,
    lawUpperItems = [],
    lawSubItems = [],
    topicType,
    relatedSumulasDocuments,
    relatedLegis,
    titleWithNickname,
    lawItemType,
  } = topic
  const definition = definitions?.edges?.[0]?.node
  const reference = referenceLaw || definition || topic

  let type: TopicDataType = 'common'
  if (referenceLaw) type = 'legislation'
  else if (definition) type = 'definition'

  const allowedLawUpperItems = lawUpperItems.filter(
    (i) => i.type && allowedLawItemTypes.includes(i.type)
  )
  const allowedLawSubItems = lawSubItems.filter(
    (i) => i.type && allowedLawItemTypes.includes(i.type)
  )

  const referenceUrl = context === 'TOPIC' ? topic.url || '' : `${reference.url}${lawAnchor || ''}`
  const image = topic.thumborImage?.current || null
  const shouldRenderImage = type === 'common' && topic.thumborImage?.originalUrl

  return {
    title: formatLawTitle(topic.title!, topic.county!, topic.state!),
    titleWithNickname: titleWithNickname
      ? titleWithNickname
      : formatLawTitle(topic.title!, topic.county!, topic.state!),
    type,
    referenceUrl,
    description: lawItemDescription,
    referenceDescription: reference.description!,
    image: shouldRenderImage ? image : null,
    allowedLawUpperItems,
    allowedLawSubItems,
    topicId: topic.tid,
    topicType,
    isRevoked: !!topic.lawItemRevoked,
    relatedLegis,
    relatedSumulasDocuments,
    lawItemType,
  }
}

useTopicData.fragments = {
  topic: gql`
    fragment UseTopicData_topic on Topic {
      title
      titleWithNickname
      description
      url
      state
      county
      lawSubItems {
        type
        description
        revoked
        hasToBeCompiled
      }
      lawUpperItems {
        type
        description
        revoked
        hasToBeCompiled
      }
      lawAnchor
      lawItemDescription
      lawItemRevoked
      lawItemType
      thumborImage {
        originalUrl
        current: thumbUrl(size: "296x152")
      }
      referenceLaw {
        url
        title
        description
      }
      definitions: lastDocuments(artifact: "DEFINICAO", first: 1) {
        edges {
          node {
            title
            description
            url
          }
        }
      }
      tid
      topicType
      relatedLegis {
        subject
        relatedLegisTitle
        prepositionForReference
      }
      relatedSumulasDocuments {
        title
      }
    }
  `,
}

function formatLawTitle(title: string, county: string, state: string): string {
  const titleCounty = county && county !== '0' ? ` - Mun. ${county}` : ''
  const titleState = state && state !== '0' ? ` (${state})` : ''
  return `${title}${titleCounty}${titleState}`
}
